import { useState, useRef } from "react";
import { CustomTypography } from "../../../../components/CustomTypography/CustomTypography";
import { Icon } from "../../../../components/Icon/Icon";
import { IconWithBackground } from "../../../../components/IconWithBackground/IconWithBackground";
import { colors } from "../../../../styles/colors";
import { dataDocuments } from "./data";
import useStyles from "./styles";
import { useTranslation } from "react-i18next";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import { useParams } from "react-router";
import {
  useGetDocumentByEmployee,
  useCreateDocument,
  documentsKeys,
} from "../../../../api/document";
import { uploadToStorage } from "../../../../api/media";
import { openDocument } from "../../../../hooks/useDocumentViewer";
import { getCurrentFleetId } from "../../../../utils/getCurrentFleet";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";

type Props = {};

/**
 * Collaborator documents card.
 *
 * Brought in line with the vehicle documents card (Vehicule/UpdateVehicule/
 * Documents), which is the reference implementation for this pattern. Three
 * things were broken here:
 *
 *  1. The identity slot was matched on the string "indentity". The backend
 *     stores "identity" (Strongbox::TYPE_IDENTITY), so the lookup never hit
 *     and the eye button did nothing at all - no open, no error, no feedback.
 *  2. Documents were rendered through react-modal-image's Lightbox fed with
 *     the raw scan_id. scan_id is frequently a MinIO object key or an expired
 *     presigned URL rather than a displayable image, and Lightbox cannot show
 *     a PDF at all, so even the permis row failed once the URL aged out.
 *     openDocument() resolves a fresh presigned URL and opens it properly.
 *  3. The eye was always rendered, including for slots with nothing behind
 *     them, which is what made this look like a dead button rather than an
 *     empty slot. Each row now states whether it holds a file and offers an
 *     upload, so a missing document is actionable instead of silent.
 */
export const DocumentsCollaborator: React.FC<Props> = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { id = "" } = useParams();
  const userId = parseInt(id);
  const fleetId = getCurrentFleetId();

  const { documents } = useGetDocumentByEmployee(fleetId, userId);

  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [pendingDocId, setPendingDocId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate: createDocument } = useCreateDocument(() => {
    queryClient.invalidateQueries({
      queryKey: documentsKeys.documentByEmployee(fleetId, userId),
    } as any);
    setUploadingId(null);
  });

  // The API is not consistent about where the type lands, so accept the same
  // shapes the vehicle card does rather than assuming `type` is always set.
  const getDocumentForSlot = (slotType: string) =>
    documents?.find((doc: any) => {
      const docType = doc.type || doc.document_type?.name || doc.name;
      return docType === slotType;
    });

  const getScanValue = (slotType: string): string | undefined => {
    const doc = getDocumentForSlot(slotType) as any;
    return doc?.scan_id || doc?.scan;
  };

  const openDocumentOrWarn = async (slotType: string) => {
    const scanValue = getScanValue(slotType);
    if (!scanValue) {
      toast.error(
        t(
          "panel.document.notAvailable",
          "This document has not been uploaded yet"
        )
      );
      return;
    }
    await openDocument(scanValue);
  };

  const handleUploadClick = (docId: string) => {
    setPendingDocId(docId);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const slot = dataDocuments.find((d) => d.id === pendingDocId);

    // Reset the input first: without this, picking the same file twice in a
    // row fires no change event and the second upload never starts.
    if (fileInputRef.current) fileInputRef.current.value = "";

    if (!file || !slot) {
      setPendingDocId(null);
      return;
    }

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/webp",
    ];
    if (!allowedTypes.includes(file.type)) {
      toast.error(
        t("panel.document.invalidFileType", "Please upload a PDF or image file")
      );
      setPendingDocId(null);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error(
        t("panel.document.fileTooLarge", "File must be smaller than 10MB")
      );
      setPendingDocId(null);
      return;
    }

    setUploadingId(slot.id);

    try {
      const result = await uploadToStorage(file, {
        directory: "strongbox",
        filename: `employee_${userId}_${slot.type}_${Date.now()}`,
      });

      createDocument({
        name: slot.type,
        scan_id: result.url,
        user_id: userId,
        type: slot.type,
      } as any);
    } catch (err) {
      toast.error(t("panel.document.uploadFailed", "Failed to upload document"));
      setUploadingId(null);
    }

    setPendingDocId(null);
  };

  const uploadedCount = dataDocuments.filter((d) => !!getScanValue(d.type))
    .length;

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".pdf,.jpg,.jpeg,.png,.webp"
        onChange={handleFileChange}
      />
      <div className={classes.container}>
        <div className={classes.containerHeader}>
          <CustomTypography
            isUppercase
            text={t("panel.document.documents", "Documents")}
            variant={"h5"}
            styles={{ containerText: classes.contratTitle }}
          />
          <span className={classes.docCount}>
            {uploadedCount}/{dataDocuments.length}
          </span>
        </div>

        {dataDocuments.map((document, index) => {
          const docHasFile = !!getScanValue(document.type);
          const isUploading = uploadingId === document.id;

          return (
            <div
              className={`${classes.subContainer} ${
                docHasFile ? classes.docAvailable : classes.docMissing
              }`}
              key={index}
            >
              <div className={classes.subContainer2}>
                <IconWithBackground iconName={document.icoName} />
                <div className={classes.docInfo}>
                  <CustomTypography
                    text={document.title}
                    isUppercase
                    variant="link"
                    fontWeight={"bold"}
                  />
                  <span
                    className={
                      docHasFile ? classes.statusUploaded : classes.statusMissing
                    }
                  >
                    {docHasFile
                      ? t("panel.document.uploaded", "Uploaded")
                      : t("panel.document.missing", "Missing")}
                  </span>
                </div>
              </div>

              <div className={classes.actionButtons}>
                {isUploading ? (
                  <CircularProgress size={20} />
                ) : (
                  <>
                    {docHasFile && (
                      <IconButton
                        aria-label={t("a11y.viewDocument", "View document")}
                        className={classes.iconButton}
                        onClick={() => openDocumentOrWarn(document.type)}
                        title={t("common.view", "View")}
                      >
                        <Icon
                          name="eye"
                          width={"20px"}
                          height={"20px"}
                          color={colors.BLACK}
                        />
                      </IconButton>
                    )}
                    <IconButton
                      aria-label={t("a11y.uploadDocument", "Upload document")}
                      className={classes.iconButton}
                      onClick={() => handleUploadClick(document.id)}
                      title={
                        docHasFile
                          ? t("panel.document.reupload", "Re-upload")
                          : t("panel.document.upload", "Upload")
                      }
                    >
                      <Icon
                        name={docHasFile ? "reload" : "plus"}
                        width={"20px"}
                        height={"20px"}
                        color={docHasFile ? colors.BLACK : colors.primary}
                      />
                    </IconButton>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};